/* eslint-disable react/no-this-in-sfc */
import React, { useState, useContext } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig, UserContext, RoleUtils } from '@galaxy/lorule-react-commons';
import { RouteComponentProps } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import NinModal from './NinModal';
import { AmCreateModalFormBaseProps, AmCreateFormType } from './amCreateformTypes';
import {
    CreateActionMemoType,
    CopyActionMemoType,
    AddSupplementActionMemoType,
} from '../../../api/actionMemoApiTypes';
import { createActionMemo, copyActionMemo, addSupplement } from '../../../api/actionMemoApi';
import ConfigContext from '../../../contexts/ConfigContext';
import AmCreateForm, { AmCreateModalForm, NO_SIGNATURE_REQUIRED_ID } from './AmCreateForm';
import { INITIAL_SELECT_ID } from '../../../utils/Constants';
import EmployeeIdContext from '../../../contexts/EmployeeIdContext';

export interface CreateActionMemoModal extends AmCreateModalFormBaseProps {
    amId?: number;
    orgId?: number;
    projectManagerId?: number;
    projectManagerName?: string;
    organizationId?: number;
    organizationName?: string;
    subject?: string;
    routerProps: RouteComponentProps;
}

const submitAmCreate = async (
    config: LoruleRequestConfig,
    formType: AmCreateFormType,
    values: AmCreateModalForm,
    roles: string[],
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
    routerProps: RouteComponentProps,
) => {
    try {
        let amInfo;
        if (formType === AmCreateFormType.CREATE) {
            const actionMemo: CreateActionMemoType = {
                subject: values.subject,
                confidential: values.confidential,
                projectManagerId: values.projectManagerId,
                templateCode: values.templateCode,
                organizationId: values.organizationId,
                contractOrganizationId:
                    values.contractOrganizationId && values.contractOrganizationId >= 0
                        ? values.contractOrganizationId
                        : undefined,
                contractSigner:
                    values.contractSigner && values.contractSigner >= 0
                        ? values.contractSigner
                        : undefined,
            };
            amInfo = await createActionMemo(config, actionMemo);
        }
        if (formType === AmCreateFormType.COPY) {
            const actionMemo: CopyActionMemoType = {
                id: values.id,
                subject: values.subject,
                confidential: values.confidential,
                projectManagerId: values.projectManagerId,
                templateCode: values.templateCode,
                organizationId: values.organizationId,
                contractOrganizationId:
                    values.contractOrganizationId && values.contractOrganizationId >= 0
                        ? values.contractOrganizationId
                        : undefined,
                contractSigner:
                    values.contractSigner && values.contractSigner >= 0
                        ? values.contractSigner
                        : undefined,
            };
            amInfo = await copyActionMemo(
                config,
                actionMemo,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
        }
        if (formType === AmCreateFormType.SUPPLEMENT) {
            const actionMemo: AddSupplementActionMemoType = {
                id: values.id,
                subject: values.subject,
                confidential: values.confidential,
                templateCode: values.templateCode,
                contractOrganizationId:
                    values.contractOrganizationId && values.contractOrganizationId >= 0
                        ? values.contractOrganizationId
                        : undefined,
                contractSigner:
                    values.contractSigner && values.contractSigner >= 0
                        ? values.contractSigner
                        : undefined,
            };
            amInfo = await addSupplement(
                config,
                actionMemo,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
        }
        if (amInfo) {
            routerProps.history.push(`/details/${amInfo.id}`);
        }

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Create failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const AmCreateModal = (props: CreateActionMemoModal) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const employeeId = useContext(EmployeeIdContext);
    const userInfo = useContext(UserContext);

    const [responseError, setResponseError] = useState('');

    const amCreateValidationSchema = Yup.object().shape({
        subject: Yup.string()
            .required('Subject is required')
            // eslint-disable-next-line no-template-curly-in-string
            .max(250, 'Subject exceeds max length of ${max} characters')
            .matches(
                /^[\x20-\x7F]*$/,
                'Subject must consist of only ASCII characters (check for MS Word hyphenation)',
            ),
        templateType: Yup.string().required('Template Type is required'),
        templateCode: Yup.string().required('Template is required'),
        projectManagerId: Yup.number().test(
            'id validation',
            'Project Manager is required',
            (projectManagerId) =>
                props.formType === AmCreateFormType.SUPPLEMENT ||
                !projectManagerId ||
                projectManagerId > 0,
        ),
        organizationId: Yup.number().test(
            'id validation',
            'Organization is required',
            (organizationId) =>
                props.formType === AmCreateFormType.SUPPLEMENT ||
                !organizationId ||
                organizationId > 0,
        ),
        contractOrganizationId: Yup.number().test(
            'contact organization id validation',
            'Contract entity is required',
            function test() {
                return (
                    this.parent.signatureRequired === NO_SIGNATURE_REQUIRED_ID ||
                    this.parent.contractOrganizationId > 0
                );
            },
        ),
        contractSigner: Yup.number().test(
            'contact organization id validation',
            'Contract signer is required',
            function test() {
                return (
                    this.parent.signatureRequired === NO_SIGNATURE_REQUIRED_ID ||
                    this.parent.contractSigner > 0
                );
            },
        ),
    });

    let projectManagerId = INITIAL_SELECT_ID;
    let projectManagerName = '';
    if (props.projectManagerId && props.projectManagerName) {
        ({ projectManagerId } = props);
        ({ projectManagerName } = props);
    } else if (employeeId && userInfo) {
        projectManagerId = employeeId;
        projectManagerName = `${userInfo.displayName} (${userInfo.jobTitle})`;
    }

    const initialValues: AmCreateModalForm = {
        id: props.amId ? props.amId : INITIAL_SELECT_ID,
        subject: props.subject ? props.subject : '',
        confidential: false,
        templateType: '',
        templateCode: '',
        projectManagerId,
        projectManagerName,
        organizationId: props.organizationId ? props.organizationId : INITIAL_SELECT_ID,
        organizationName: props.organizationName ? props.organizationName : '',
        confidentialFlag: INITIAL_SELECT_ID,
        signatureRequired: NO_SIGNATURE_REQUIRED_ID,
        contractOrganizationId: INITIAL_SELECT_ID,
        contractSigner: INITIAL_SELECT_ID,
    };

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={amCreateValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitAmCreate(
                            config,
                            props.formType,
                            values,
                            roles,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                            props.routerProps,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={(formikProps) => (
                        <AmCreateForm
                            {...formikProps}
                            orgId={props.orgId}
                            formType={props.formType}
                            responseError={responseError}
                            setResponseError={setResponseError}
                            toggleModal={toggleModal}
                        />
                    )}
                />
            )}
        />
    );
};

export default AmCreateModal;
